import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { alertKind, cleanSoundPolicy, DEFAULT_ALERT_SOUNDS, nearestFlash, TONE_SILENT } from '@ww/shared';
import type { AlertKind, AlertSoundPolicy, TenantAlert } from '@ww/shared';
import { api } from '../api';
import { useLive } from './useLive';
import {
  getSoundPrefs,
  isSilenced,
  setSoundPrefs,
  silenceRemainingMs,
  type SoundPrefs,
} from '../lib/soundPrefs';
import { audioState, playTone, unlockAudio, type AudioState } from '../lib/tonePlayer';

/**
 * Sounds an alert when one arrives.
 *
 * Mounted inside LiveProvider and OUTSIDE Shell, deliberately: the wallboard renders
 * without the shell, and the wallboard is the screen most likely to be the only thing
 * anyone is looking at.
 *
 * Two halves that must not be confused:
 *  - the TENANT policy (which tone for which kind) comes from the server and is audited;
 *  - the DEVICE prefs (muted, snoozed, volume) live in this browser only.
 * See `lib/soundPrefs.ts` for why.
 */

export interface SoundContextValue {
  /** Tenant policy, or the defaults while it loads. */
  policy: AlertSoundPolicy;
  policyLoaded: boolean;
  reloadPolicy: () => Promise<void>;
  prefs: SoundPrefs;
  setPrefs: (patch: Partial<SoundPrefs>) => void;
  /** Mute this device for N minutes. */
  snooze: (minutes: number) => void;
  /** Clear both the indefinite mute and any running snooze. */
  unmute: () => void;
  /** Ticks down while a snooze runs, so the UI can show the remaining time. */
  silenceMsLeft: number;
  /** True when nothing would sound right now, for whatever reason. */
  silenced: boolean;
  audio: AudioState;
  /** Call from a click. Resolves once the browser has actually allowed audio. */
  enableAudio: () => Promise<void>;
  /** Play a tone now, ignoring mute. For the preview buttons on the settings page. */
  preview: (toneId: string) => void;
  /** When the last alert tone played, for "last sounded at" in the UI. Null if never. */
  lastSoundedAt: number | null;
}

const SoundContext = createContext<SoundContextValue | null>(null);

/**
 * Loudest wins. When four alerts land in the same poll we play ONE tone, for the worst
 * of them — four overlapping tones is noise, and noise is what gets speakers unplugged.
 * Mirrors KIND_ORDER in WeatherMap so the sound agrees with the map.
 */
const KIND_RANK: Record<AlertKind, number> = {
  watch: 0,
  sps: 1,
  snow: 2,
  flood: 3,
  svr: 4,
  ice: 5,
  tor: 6,
};

/**
 * Floor between two alert tones.
 *
 * The 60-second fallback poll runs alongside SSE, so a burst of warnings can produce
 * several arrivals within a second or two. Without this the room gets a pile-up.
 */
const MIN_GAP_MS = 4_000;

export function SoundProvider({ children }: { children: ReactNode }) {
  const { dashboard, tenant, lightning, assets } = useLive();
  const tenantId = tenant?._id ?? null;

  const [policy, setPolicy] = useState<AlertSoundPolicy>(DEFAULT_ALERT_SOUNDS);
  const [policyLoaded, setPolicyLoaded] = useState(false);
  const [prefs, setPrefsState] = useState<SoundPrefs>(() => getSoundPrefs(tenantId));
  const [audio, setAudio] = useState<AudioState>(() => audioState());
  const [silenceMsLeft, setSilenceMsLeft] = useState(0);
  const [lastSoundedAt, setLastSoundedAt] = useState<number | null>(null);

  /*
   * `seen` maps nwsId -> alert.version. The version is what lets a re-issue be told
   * apart from the same alert being re-listed, which happens on every one of the
   * dashboard refetches. Held in a ref because the SSE effect in useLive is frozen on
   * [userId, actingTenantId] and anything captured in state would go stale there.
   */
  const seen = useRef<Map<string, string>>(new Map());
  /** Until the first snapshot has been absorbed, nothing is "new" — see below. */
  const primed = useRef(false);
  const lastPlayAt = useRef(0);
  const policyRef = useRef(policy);
  const prefsRef = useRef(prefs);
  policyRef.current = policy;
  prefsRef.current = prefs;

  // ---- tenant policy ----

  const reloadPolicy = useCallback(async () => {
    try {
      const r = await api.alertSounds();
      setPolicy(cleanSoundPolicy(r.sounds));
    } catch {
      // A failed load leaves the defaults in place. Falling back to "no sound" would be
      // the wrong failure: a county that configured a tornado tone should still get one.
      setPolicy(DEFAULT_ALERT_SOUNDS);
    } finally {
      setPolicyLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!tenantId) return;
    void reloadPolicy();
  }, [tenantId, reloadPolicy]);

  // ---- device prefs ----

  // Switching tenants loads that tenant's mute, and re-arms the "new alert" detector:
  // the incoming county's alerts are all unseen, and every one of them is not news.
  useEffect(() => {
    setPrefsState(getSoundPrefs(tenantId));
    seen.current = new Map();
    primed.current = false;
  }, [tenantId]);

  const setPrefs = useCallback(
    (patch: Partial<SoundPrefs>) => {
      setPrefsState(setSoundPrefs(tenantId, patch));
    },
    [tenantId],
  );

  const snooze = useCallback(
    (minutes: number) => setPrefs({ muted: false, mutedUntil: Date.now() + minutes * 60_000 }),
    [setPrefs],
  );
  const unmute = useCallback(() => setPrefs({ muted: false, mutedUntil: null }), [setPrefs]);

  // Count a running snooze down, and let it lapse on its own.
  useEffect(() => {
    const tick = () => {
      const left = silenceRemainingMs(prefsRef.current);
      setSilenceMsLeft(left);
      // Expired: rewrite the pref so the UI is not left showing a mute that is over.
      if (left === 0 && prefsRef.current.mutedUntil !== null) {
        setPrefsState(setSoundPrefs(tenantId, { mutedUntil: null }));
      }
    };
    tick();
    const t = window.setInterval(tick, 1_000);
    return () => window.clearInterval(t);
  }, [tenantId, prefs.mutedUntil, prefs.muted]);

  // ---- audio unlock ----

  const enableAudio = useCallback(async () => {
    setAudio(await unlockAudio());
  }, []);

  /*
   * A page reload drops the unlock, so re-arm on the first interaction of any kind.
   * This is not a way around the autoplay rule — it IS the rule, satisfied: a real
   * gesture happened, we just did not make the operator aim at a specific button. The
   * explicit "Enable sound" control stays visible until this fires, because on an
   * untouched wallboard it never will.
   */
  useEffect(() => {
    if (audio !== 'blocked') return;
    const onGesture = () => void enableAudio();
    const opts = { once: true, capture: true } as const;
    window.addEventListener('pointerdown', onGesture, opts);
    window.addEventListener('keydown', onGesture, opts);
    return () => {
      window.removeEventListener('pointerdown', onGesture, opts);
      window.removeEventListener('keydown', onGesture, opts);
    };
  }, [audio, enableAudio]);

  const preview = useCallback((toneId: string) => {
    // Ignores mute on purpose: pressing preview IS the request to hear it.
    playTone(toneId, prefsRef.current.volume);
  }, []);

  // ---- the detector ----

  useEffect(() => {
    const rows = dashboard?.alerts;
    if (!rows) return;

    const next = new Map<string, string>();
    const arrivals: TenantAlert[] = [];
    for (const row of rows) {
      const id = row.alert.nwsId;
      const version = row.alert.version ?? row.alert.sent ?? '';
      next.set(id, version);
      const before = seen.current.get(id);
      if (before === undefined) arrivals.push(row);
      else if (before !== version && policyRef.current.soundOnChange) arrivals.push(row);
    }
    seen.current = next;

    /*
     * The first snapshot after login, a reload or a tenant switch is the CURRENT state
     * of the world, not news. Sounding it would mean every page refresh during an
     * active tornado warning re-alarms the room, which teaches people to refresh less —
     * the opposite of what this dashboard is for.
     */
    if (!primed.current) {
      primed.current = true;
      return;
    }
    if (!arrivals.length) return;

    const p = policyRef.current;
    if (!p.enabled) return;
    if (isSilenced(prefsRef.current)) return;
    if (audioState() !== 'ready') return;

    const now = Date.now();
    if (now - lastPlayAt.current < MIN_GAP_MS) return;

    // One tone, for the worst thing that arrived.
    let worst: AlertKind | null = null;
    for (const row of arrivals) {
      const kind = alertKind(row.alert.event, !!row.alert.parameters?.iceAccumulationIn);
      if (!worst || KIND_RANK[kind] > KIND_RANK[worst]) worst = kind;
    }
    if (!worst) return;

    const toneId = p.tones[worst] ?? TONE_SILENT;
    if (toneId === TONE_SILENT) return;

    const played = playTone(toneId, prefsRef.current.volume);
    if (played > 0) {
      lastPlayAt.current = now;
      setLastSoundedAt(now);
    }
  }, [dashboard]);

  /*
   * Lightning closing in on an asset.
   *
   * Separate from the alert detector above because lightning is an observation, not an
   * alert: there may be no warning at all, and the same storm will keep producing
   * flashes for an hour. So this fires on CROSSING the threshold — quiet while it stays
   * close, and armed again only once it has cleared the distance — rather than on every
   * new flash, which would beep every ninety seconds through a summer storm and get the
   * speakers unplugged.
   */
  const ltgArmed = useRef(true);
  useEffect(() => {
    const p = policyRef.current;
    const miles = p.lightningNearMiles;
    if (!p.enabled || !miles || p.lightningTone === TONE_SILENT) return;
    if (!lightning?.length || !assets?.length) return;

    let closest = Infinity;
    for (const a of assets) {
      if (!a.enabled) continue;
      const n = nearestFlash(lightning, a.location.coordinates);
      if (n && n.miles < closest) closest = n.miles;
    }

    // Hysteresis: re-arm only once it is clearly away again, so a flash hovering on the
    // threshold cannot chatter the tone on and off.
    if (closest > miles * 1.5) {
      ltgArmed.current = true;
      return;
    }
    if (closest > miles || !ltgArmed.current) return;

    if (isSilenced(prefsRef.current)) return;
    if (audioState() !== 'ready') return;
    const now = Date.now();
    if (now - lastPlayAt.current < MIN_GAP_MS) return;

    if (playTone(p.lightningTone, prefsRef.current.volume) > 0) {
      ltgArmed.current = false;
      lastPlayAt.current = now;
      setLastSoundedAt(now);
    }
  }, [lightning, assets]);

  const silenced = isSilenced(prefs) || !policy.enabled || audio !== 'ready';

  const value = useMemo<SoundContextValue>(
    () => ({
      policy,
      policyLoaded,
      reloadPolicy,
      prefs,
      setPrefs,
      snooze,
      unmute,
      silenceMsLeft,
      silenced,
      audio,
      enableAudio,
      preview,
      lastSoundedAt,
    }),
    [
      policy,
      policyLoaded,
      reloadPolicy,
      prefs,
      setPrefs,
      snooze,
      unmute,
      silenceMsLeft,
      silenced,
      audio,
      enableAudio,
      preview,
      lastSoundedAt,
    ],
  );

  return <SoundContext.Provider value={value}>{children}</SoundContext.Provider>;
}

export function useAlertSound(): SoundContextValue {
  const ctx = useContext(SoundContext);
  if (!ctx) throw new Error('useAlertSound must be used inside <SoundProvider>');
  return ctx;
}
